'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Reveal from '@/components/common/Reveal';

// "We Are Proud Members of" - a continuously scrolling logo strip.
//
// Auto-running with no arrow buttons, as asked. Two accessibility
// consequences are handled here rather than left implicit:
//
//  1. WCAG 2.2.2 (Pause, Stop, Hide). Anything that moves automatically for
//     more than five seconds needs a way to stop it. With no arrows there is
//     nowhere obvious to put a control, so: it pauses on hover and on keyboard
//     focus, stops entirely under prefers-reduced-motion, and carries a
//     screen-reader-only toggle that becomes visible when focused.
//  2. The strip is rendered twice so the loop is seamless. The second copy is
//     aria-hidden, otherwise every organisation is announced twice.
//
// The list is the accessible content; the marquee is presentation.

interface Member {
  slug: string;
  name: string;
}

const MEMBERS: Member[] = [
  { slug: 'mca', name: 'Ministry of Corporate Affairs, Government of India' },
  { slug: 'msme', name: 'Micro, Small and Medium Enterprises (MSME), Government of India' },
  { slug: 'gst', name: 'Goods and Services Tax (GST), India' },
  { slug: 'dgft', name: 'Directorate General of Foreign Trade (DGFT), Government of India' },
  { slug: 'icegate', name: 'ICEGATE, the Indian Customs national trade portal' },
  { slug: 'fssai', name: 'Food Safety and Standards Authority of India (FSSAI)' },
  { slug: 'fieo', name: 'Federation of Indian Export Organisations (FIEO)' },
  { slug: 'icc', name: 'Indian Chamber of Commerce (ICC)' },
];

/** One pass of the strip. The duplicate is hidden from assistive tech. */
function Strip({ ariaHidden = false }: { ariaHidden?: boolean }) {
  return (
    <ul
      className="flex shrink-0 items-center gap-12 pr-12 lg:gap-16 lg:pr-16"
      {...(ariaHidden ? { 'aria-hidden': true } : {})}
    >
      {MEMBERS.map((m) => (
        <li key={m.slug} className="shrink-0">
          <div className="relative h-12 w-auto lg:h-14">
            <AppImage
              src={`/assets/members/${m.slug}.webp`}
              alt={ariaHidden ? '' : m.name}
              width={220}
              height={56}
              loading="lazy"
              className="h-12 w-auto object-contain lg:h-14"
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function MembershipsSection() {
  const [paused, setPaused] = useState(false);

  return (
    <section className="border-y border-border bg-surface/30 py-10 lg:py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mb-8 text-center">
          <h2 className="font-headline text-xl font-bold text-foreground md:text-2xl">
            We Are Proud Members of
          </h2>
        </Reveal>
      </div>

      <div
        className="group relative"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
      >
        {/* Fades so logos enter and leave rather than being cut off. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-linear-to-r from-background to-transparent lg:w-28"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-linear-to-l from-background to-transparent lg:w-28"
        />

        <div className="flex overflow-hidden">
          <div className={`fd-marquee flex ${paused ? 'is-paused' : ''}`}>
            <Strip />
            <Strip ariaHidden />
          </div>
        </div>

        <div className="mx-auto mt-6 flex max-w-7xl justify-center px-4">
          <button
            type="button"
            onClick={() => setPaused((p) => !p)}
            className="sr-only focus:not-sr-only focus:flex focus:min-h-11 focus:items-center focus:rounded-full focus:border focus:border-border focus:bg-surface focus:px-4 focus:font-cta focus:text-sm focus:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            {paused ? 'Resume the logo carousel' : 'Pause the logo carousel'}
          </button>
        </div>
      </div>

      <style jsx>{`
        .fd-marquee {
          animation: fdMarquee 45s linear infinite;
          width: max-content;
          /* Without this the flex parent shrinks it back to the container
             width, so translateX(-50%) moves half a viewport instead of one
             full strip and the loop visibly jumps every cycle. */
          flex-shrink: 0;
        }
        .fd-marquee.is-paused {
          animation-play-state: paused;
        }
        @keyframes fdMarquee {
          from {
            transform: translateX(0);
          }
          to {
            /* Exactly one strip width, so the second copy lands where the
               first began and the loop is invisible. */
            transform: translateX(-50%);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .fd-marquee {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
}
